import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";

const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");
  const qc = useQueryClient();

  useEffect(() => {
    if (!sid) return setState("error");
    let n = 0;
    let t;
    const poll = async () => {
      try {
        const s = await getPaymentStatus(sid);
        if (s.payment_status === "paid") {
          qc.invalidateQueries({ queryKey: ["pro"] });
          return setState("paid");
        }
        if (s.status === "expired" || s.payment_status === "failed") return setState("error");
      } catch {
        return setState("error");
      }
      if (++n >= MAX_POLLS) return setState("timeout");
      t = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(t);
  }, [sid, qc]);

  const view = {
    checking: [Loader2, "Confirming your payment...", "This only takes a few seconds.", "animate-spin text-amber-500"],
    paid: [CheckCircle2, "You're GearHead Pro!", "Unlimited AI Mechanic messages are now unlocked.", "text-emerald-400"],
    timeout: [Loader2, "Still processing", "Your payment is taking longer than usual. Refresh this page in a minute.", "text-amber-500"],
    error: [XCircle, "Payment not completed", "We couldn't confirm your payment. You have not been charged twice - feel free to try again.", "text-red-400"],
  }[state];
  const [Icon, title, text, cls] = view;

  return (
    <main className="max-w-xl mx-auto px-4 py-28 text-center" data-testid="payment-success-page">
      <Icon size={56} className={`mx-auto ${cls}`} />
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase mt-6" data-testid="payment-status-title">{title}</h1>
      <p className="text-slate-400 mt-4" data-testid="payment-status-text">{text}</p>
      {state !== "checking" && (
        <Link to="/ask" data-testid="payment-go-chat-btn" className="mt-10 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0B0D10] font-display font-bold uppercase px-6 h-12 rounded-sm transition-colors duration-200">
          <Sparkles size={16} /> Go to AI Mechanic
        </Link>
      )}
    </main>
  );
}
